/*
 * Chat Session Service
 * Features: Session management, message persistence, active session handling, auto-session creation
 * Logic: Manages chat sessions in database via API endpoints
 * Changes:
 * - Added one transactional edited-message replacement request
 * - Added durable tool-call updates for canonicalized pending assistant messages
 * - Allows persisted assistant tool-call content to be null at the DTO boundary
 * - Initial implementation with full session lifecycle management
 * - Added ensureActiveSession() method to auto-create sessions for users without one
 * - Ensures seamless chat experience by guaranteeing session availability
 * - Updated ChatSession interface from PascalCase to camelCase (id, userId, title, isActive, messageCount, lastMessageAt, etc.)
 * - Updated deleteSession return type from NewActiveSessionId to newActiveSessionId
 * - Added setActiveSession() method for dedicated active session switching using PATCH endpoint
 * - Modified updateSession() to only handle title updates, no longer handles isActive parameter
 */

import { request } from './dataService';

export interface ChatSession {
  id: number;
  userId: number;
  title: string;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  isActive: boolean;
  messageCount: number;
  lastMessageAt?: string;
}

export interface ChatMessageDTO {
  id?: number;
  chatSessionId: number;
  messageId: number;
  role: string;
  content: string | null;
  timestamp: string;
  name?: string;
  toolCallId?: string;
  toolCalls?: string; // JSON string
  executionSource?: string;
  functionData?: string; // JSON string
  isUIOnly?: boolean;
  isUiOnly?: boolean; // Generic response normalization of SQL's IsUIOnly field
  sseEventType?: string;
  eventData?: string; // JSON string
  createdAt?: string;
  createdBy?: number;
}

class ChatSessionService {
  /**
   * Get all sessions for current user
   */
  async getSessions(): Promise<ChatSession[]> {
    const response = await request('get /api/data/chat/sessions');
    return response as ChatSession[];
  }

  /**
   * Get active session for current user
   */
  async getActiveSession(): Promise<ChatSession | null> {
    const response = await request('get /api/data/chat/sessions/active');
    const sessions = response as ChatSession[];
    return sessions.length > 0 ? sessions[0] : null;
  }

  /**
   * Get or create active session for current user
   * Ensures user always has an active session
   */
  async ensureActiveSession(): Promise<ChatSession> {
    const response = await request('get /api/data/chat/sessions/active/ensure');
    const sessions = response as ChatSession[];
    if (!sessions || sessions.length === 0) {
      throw new Error('Failed to get or create active session');
    }
    return sessions[0];
  }

  /**
   * Get specific session by ID
   */
  async getSession(id: number): Promise<ChatSession> {
    const response = await request(`get /api/data/chat/sessions/${id}`);
    const sessions = response as ChatSession[];
    if (sessions.length === 0) {
      throw new Error(`Session ${id} not found`);
    }
    return sessions[0];
  }

  /**
   * Create new session
   */
  async createSession(title?: string, isActive = true): Promise<ChatSession> {
    const response = await request('post /api/data/chat/sessions', {
      title,
      isActive,
    });
    const sessions = response as ChatSession[];
    if (sessions.length === 0) {
      throw new Error('Failed to create session');
    }
    return sessions[0];
  }

  /**
   * Update session title (for renaming)
   */
  async updateSession(id: number, title: string): Promise<void> {
    await request(`put /api/data/chat/sessions/${id}`, {
      title,
    });
  }

  /**
   * Set session as active (for switching sessions)
   */
  async setActiveSession(id: number): Promise<void> {
    await request(`patch /api/data/chat/sessions/${id}/active`, {});
  }

  /**
   * Delete session
   */
  async deleteSession(id: number): Promise<{ newActiveSessionId?: number }> {
    const response = await request(`delete /api/data/chat/sessions/${id}`);
    return response as { newActiveSessionId?: number };
  }

  /**
   * Get all messages for a session (includes UI-only)
   */
  async getMessages(sessionId: number): Promise<ChatMessageDTO[]> {
    const response = await request('get /api/data/chat/sessions/:chatSessionId/messages', {
      chatSessionId: sessionId
    });
    return response as ChatMessageDTO[];
  }

  /**
   * Bypass GET caching when an edit result must be reconciled authoritatively.
   */
  async getMessagesAuthoritative(sessionId: number): Promise<ChatMessageDTO[]> {
    const response = await request(
      'post /api/data/chat/sessions/:chatSessionId/messages/reconcile',
      { chatSessionId: sessionId }
    );
    return response as ChatMessageDTO[];
  }

  /**
   * Get messages for LLM context (excludes UI-only)
   */
  async getMessagesForLLM(sessionId: number): Promise<ChatMessageDTO[]> {
    const response = await request('get /api/data/chat/sessions/:chatSessionId/messages/llm', {
      chatSessionId: sessionId
    });
    return response as ChatMessageDTO[];
  }

  /**
   * Insert single message
   */
  async insertMessage(sessionId: number, message: Omit<ChatMessageDTO, 'id' | 'chatSessionId' | 'createdAt' | 'createdBy'>): Promise<number> {
    const response = await request('post /api/data/chat/sessions/:chatSessionId/messages', {
      chatSessionId: sessionId,
      messageId: message.messageId,
      role: message.role,
      content: message.content,
      timestamp: message.timestamp,
      name: message.name,
      toolCallId: message.toolCallId,
      toolCalls: message.toolCalls,
      executionSource: message.executionSource,
      functionData: message.functionData,
      isUIOnly: message.isUIOnly,
      sseEventType: message.sseEventType,
      eventData: message.eventData,
    });
    const result = response as any;
    return result.id;
  }

  /**
   * Insert multiple messages (batch import)
   */
  async insertMessageBatch(sessionId: number, messages: Omit<ChatMessageDTO, 'id' | 'chatSessionId' | 'createdAt' | 'createdBy'>[]): Promise<number> {
    const messagesJson = JSON.stringify(messages.map(msg => ({
      MessageId: msg.messageId,
      Role: msg.role,
      Content: msg.content,
      Timestamp: msg.timestamp,
      Name: msg.name,
      ToolCallId: msg.toolCallId,
      ToolCalls: msg.toolCalls,
      ExecutionSource: msg.executionSource,
      FunctionData: msg.functionData,
      IsUIOnly: msg.isUIOnly,
      SSEEventType: msg.sseEventType,
      EventData: msg.eventData,
    })));

    const response = await request('post /api/data/chat/sessions/:chatSessionId/messages/batch', {
      chatSessionId: sessionId,
      messages: messagesJson,
    });
    const result = response as any;
    return result.insertedCount;
  }

  /**
   * Clear all messages from session
   */
  async clearMessages(sessionId: number): Promise<number> {
    const response = await request('delete /api/data/chat/sessions/:chatSessionId/messages', {
      chatSessionId: sessionId
    });
    const result = response as any;
    return result.DeletedCount;
  }

  /**
   * Delete messages after specific MessageId (for editing)
   */
  async deleteMessagesAfter(sessionId: number, fromMessageId: number): Promise<number> {
    const response = await request('delete /api/data/chat/sessions/:chatSessionId/messages/:messageId', {
      chatSessionId: sessionId,
      messageId: fromMessageId
    });
    const result = response as any;
    return result.DeletedCount;
  }

  /**
   * Update message content (for editing)
   */
  async updateMessageContent(sessionId: number, messageId: number, content: string): Promise<void> {
    try {
      await request('patch /api/data/chat/sessions/:chatSessionId/messages/:messageId', {
        chatSessionId: sessionId,
        messageId: messageId,
        content: content
      });
    } catch (error) {
      console.error('❌ [UPDATE MESSAGE] API error:', error);
      throw error;
    }
  }

  /**
   * Atomically update an edited message and delete every later message.
   */
  async replaceEditedMessageBranch(
    sessionId: number,
    messageId: number,
    content: string
  ): Promise<void> {
    await request('patch /api/data/chat/sessions/:chatSessionId/messages/:messageId/edit', {
      chatSessionId: sessionId,
      messageId,
      content
    });
  }

  /**
   * Update assistant tool calls after pending input capability is canonicalized.
   */
  async canonicalizeMessageToolCall(
    sessionId: number,
    messageDatabaseId: number,
    toolCallId: string
  ): Promise<void> {
    try {
      await request('patch /api/data/chat/sessions/:chatSessionId/messages/:id/tool-calls', {
        chatSessionId: sessionId,
        id: messageDatabaseId,
        toolCallId
      });
    } catch (error) {
      console.error('ChatSessionService: Failed to canonicalize message tool call', error);
      throw error;
    }
  }

  /**
   * Clear tool calls from a message (used after user interaction completes)
   */
  async clearMessageToolCalls(sessionId: number, messageId: number): Promise<void> {
    try {
      console.log('🧹 [CLEAR TOOL CALLS] Calling API:', {
        sessionId,
        messageId
      });

      const result = await request('patch /api/data/chat/sessions/:chatSessionId/messages/:messageId/clear-tool-calls', {
        chatSessionId: sessionId,
        messageId: messageId
      });

      console.log('✅ [CLEAR TOOL CALLS] API response:', result);
    } catch (error) {
      console.error('❌ [CLEAR TOOL CALLS] API error:', error);
      throw error;
    }
  }

  /**
   * Delete specific messages by their message IDs
   */
  async deleteMessagesByIds(sessionId: number, messageIds: number[]): Promise<number> {
    try {
      console.log('🗑️ [DELETE MESSAGES] Calling API:', {
        sessionId,
        messageIds
      });

      const result = await request('delete /api/data/chat/sessions/:chatSessionId/messages/by-ids', {
        chatSessionId: sessionId,
        messageIds: messageIds
      });

      console.log('✅ [DELETE MESSAGES] API response:', result);
      return (result as any).DeletedCount || 0;
    } catch (error) {
      console.error('❌ [DELETE MESSAGES] API error:', error);
      throw error;
    }
  }
}

export const chatSessionService = new ChatSessionService();
